import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Pagination';
import RecordingList from '../components/RecordingList';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { recordingService } from '../services/api';

const Recordings = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchRecordings = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await recordingService.getRecordings(
          currentPage,
          searchTerm || null,
          filter !== 'all' ? filter : null
        );

        setRecordings(response.items || []);
        setTotal(response.total || 0);
        setTotalPages(response.pages || 1);
      } catch (error) {
        console.error('Error fetching recordings:', error);
        setError('Failed to load recordings. Please try again.');
        setRecordings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecordings();
  }, [currentPage, searchTerm, filter]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('recordings.title', 'Phone Call Recordings')}</h1>
        <div className="flex space-x-4">
          <select
            value={filter}
            onChange={handleFilterChange}
            className="border rounded-md p-2"
          >
            <option value="all">{t('recordings.filter.all', 'All Recordings')}</option>
            <option value="processed">{t('recordings.filter.processed', 'Processed')}</option>
            <option value="pending">{t('recordings.filter.pending', 'Pending')}</option>
            <option value="skipped">{t('recordings.filter.skipped', 'Skipped')}</option>
          </select>
        </div>
      </div>

      <div className="mb-6">
        <SearchBar
          onSearch={handleSearch}
          placeholder={t('recordings.search.placeholder', 'Search by phone number or transcript...')}
        />
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center my-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : recordings.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            {searchTerm || filter !== 'all'
              ? t('recordings.noResults', 'No recordings found matching your criteria')
              : t('recordings.noRecordings', 'No phone call recordings found')
            }
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              {t('recordings.showing', 'Showing {{start}} to {{end}} of {{total}} recordings', {
                start: (currentPage - 1) * 10 + 1,
                end: Math.min(currentPage * 10, total),
                total: total
              })}
            </p>
          </div>
          <RecordingList recordings={recordings} />
          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Recordings;
